import api from './axiosConfig';
import { secureStorage } from 'services/secure-storage.service';

interface UpdateProfileData {
  id: number;
  name: string;
  lastname: string;
  email: string;
}

interface UpdatePasswordData {
  id: number;
  oldpassword: string;
  password: string;
}

interface StoredCredentials {
  username: string;
  expiresAt: number;
}

class AuthService {
  private readonly REMEMBER_KEY = 'remembered_user';
  private readonly EXPIRATION_DAYS = 30;

  getCurrentUser() {
    const userStr = secureStorage.getItem('user');
    if (userStr) return JSON.parse(userStr);

    return null;
  }

  private getAvatarFromRoles(roles: string[]): string {
    const rolesStr = roles.join(', ');
    if (rolesStr.includes('SYSTEMADMIN')) return 'avatar1';
    if (rolesStr.includes('OPSADMIN')) return 'avatar2';
    if (rolesStr.includes('QAADMIN')) return 'avatar3';
    if (rolesStr.includes('QAMEMBER')) return 'avatar4';
    return 'avatar5';
  }

  // Login local
  login(username: string, password: string, rememberMe: boolean = false) {
    return api
      .post('auth/signin', {
        username,
        password
      })
      .then(response => {
        if (response.data.accessToken) {
          secureStorage.setItem('user', JSON.stringify(response.data));
          const avatar = this.getAvatarFromRoles(response.data.roles);
          secureStorage.setItem('avatar', avatar);

          if (rememberMe) {
            this.storeRememberedUser({
              username: username,
              expiresAt: this.calculateExpirationDate()
            });
          } else {
            // Si rememberMe está desmarcado, limpiamos las credenciales recordadas
            console.log('Limpiando remembered_user'); // Añadir este log
            this.clearRememberedUser();
          }
        }
        return response.data;
      });
  }

  // Login SAML. Método para manejar la respuesta SAML de Azure o ManageEngine
  handleSamlCallback = (token: string) => {
    return api.post('auth/saml/callback', { token }).then(response => {
      if (response.data.accessToken) {
        secureStorage.setItem('user', JSON.stringify(response.data));
        const avatar = this.getAvatarFromRoles(response.data.roles);
        secureStorage.setItem('avatar', avatar);
      }
      return response.data;
    });
  };

  verifyTFA(username: string, otp: string) {
    return api.post('auth/verify-tfa', { username, otp }).then(response => {
      if (response.data.accessToken) {
        secureStorage.setItem('user', JSON.stringify(response.data));
        const avatar = this.getAvatarFromRoles(response.data.roles);
        secureStorage.setItem('avatar', avatar);
      }
      return response.data;
    });
  }

  logout() {
    secureStorage.removeItem('user');
    secureStorage.removeItem('avatar');
  }

  // Remember Me functionality
  private calculateExpirationDate(): number {
    const date = new Date();
    date.setDate(date.getDate() + this.EXPIRATION_DAYS);
    return date.getTime();
  }

  private storeRememberedUser(credentials: StoredCredentials): void {
    secureStorage.setItem(this.REMEMBER_KEY, JSON.stringify(credentials));
  }

  getRememberedUser(): StoredCredentials | null {
    try {
      const storedData = secureStorage.getItem(this.REMEMBER_KEY);
      if (!storedData) return null;

      const credentials: StoredCredentials = JSON.parse(storedData);

      if (new Date().getTime() > credentials.expiresAt) {
        this.clearRememberedUser();
        return null;
      }

      return credentials;
    } catch {
      return null;
    }
  }

  clearRememberedUser(): void {
    secureStorage.removeItem('remembered_user');
  }

  //nuevo usuario
  newaccount(
    username: string,
    name: string,
    lastname: string,
    email: string,
    password: string,
    activation_code: number,
    roles: string[]
  ) {
    return api.post('auth/signup', {
      username,
      name,
      lastname,
      email,
      password,
      activation_code,
      roles
    });
  }

  //actualizar usuario por el admin
  updateoneaccount(
    id: number,
    username: string,
    name: string,
    lastname: string,
    email: string,
    activation_code: number,
    roles: string[]
  ) {
    return api.put('auth/updateoneaccount', {
      id,
      username,
      name,
      lastname,
      email,
      activation_code,
      roles
    });
  }

  // Métodos para verificar si el usuario es SAML
  isSamlUser() {
    const user = this.getCurrentUser();
    return user?.auth_type?.startsWith('saml');
  }
  isAzureUser() {
    const user = this.getCurrentUser();
    return user?.auth_type === 'saml-azure';
  }
  isManageEngineUser() {
    const user = this.getCurrentUser();
    return user?.auth_type === 'saml-manageengine';
  }

  //any local user can update his personal data
  updateprofile = async (data: UpdateProfileData) => {
    if (this.isAzureUser()) {
      throw new Error('Profile update not available for Azure AD users');
    }
    if (this.isManageEngineUser()) {
      throw new Error(
        'Profile update not available for ManageEngine ADS users'
      );
    }
    const response = await api.put(`${data.id}/profileupdate`, {
      name: data.name,
      lastname: data.lastname,
      email: data.email
    });
    return response.data;
  };

  //any user can update his password
  updatepassword = async (data: UpdatePasswordData) => {
    if (this.isAzureUser()) {
      throw new Error('Password change not available for Azure AD users');
    }
    if (this.isManageEngineUser()) {
      throw new Error(
        'Password change not available for ManageEngine ADS users'
      );
    }
    const response = await api.put(`${data.id}/passwordupdate`, {
      oldpassword: data.oldpassword,
      password: data.password
    });
    return response.data;
  };

  //admin can reset any user password
  resetPassword = async (id: number, password: string) => {
    try {
      const response = await api.put(`${id}/resetpassword/`, {
        password: password
      });
      return response.data;
    } catch (error) {
      console.error(`Error resetting password for ID ${id}:`, error);
      throw error; // Optional: You can choose to rethrow the error or handle it differently
    }
  };

  //admin puede modificar los roles de usuarios SAML
  updateUserRoles = async (id: number, roles: string[]) => {
    const response = await api.put('auth/updateuserroles', {
      id,
      roles
    });
    return response.data;
  };
}

export default new AuthService();
